import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { trackLead } from "@/lib/analytics";

function fireMetaLead(email) {
  // Fires BOTH Meta Pixel Lead and AppLovin generate_lead.
  trackLead({ email, contentName: "Subscribe Form" });
}

function getUTMs() {
  try {
    const params = new URLSearchParams(window.location.search);
    return {
      utm_source: params.get("utm_source") || "",
      utm_campaign: params.get("utm_campaign") || "",
      utm_medium: params.get("utm_medium") || "",
    };
  } catch (_) {
    return { utm_source: "", utm_campaign: "", utm_medium: "" };
  }
}

/**
 * Reusable subscribe form.
 * variant: "light" (default, for cream backgrounds) | "dark" (for dark backgrounds)
 */
export default function SubscribeForm({
  productContext = "subscribe-form",
  variant = "light",
  buttonLabel = "Subscribe",
  showTrustLine = true,
  testIdPrefix = "subscribe",
}) {
  const [email, setEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email.");
      return;
    }
    setSubmitting(true);
    try {
      const utms = getUTMs();
      await api.post("/leads", {
        email,
        page: window.location.pathname,
        product_context: productContext,
        referrer: document.referrer || "",
        ...utms,
      });
      fireMetaLead(email);
      setDone(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid={`${testIdPrefix}-success`} className="text-center py-2">
        <p className={`font-bold text-lg ${variant === "dark" ? "text-white" : "text-[#1E1E24]"}`}>
          <span aria-hidden="true">✓</span> You&apos;re in — check your inbox
        </p>
      </div>
    );
  }

  const inputCls =
    variant === "dark"
      ? "flex-1 px-5 py-3 rounded-full border-2 border-white bg-white text-[#1E1E24] focus:outline-none focus:ring-4 focus:ring-[#FFB400] font-medium text-base"
      : "flex-1 px-5 py-3 rounded-full border-2 border-[#1E1E24] bg-white text-[#1E1E24] focus:outline-none focus:ring-4 focus:ring-[#FFB400] font-medium text-base";

  const btnCls =
    variant === "dark"
      ? "bg-[#FFB400] text-[#1E1E24] border-2 border-[#FFB400] rounded-full hover:bg-white hover:border-white transition-all font-bold tracking-wide px-8 py-3 disabled:opacity-60 whitespace-nowrap"
      : "bg-[#1E1E24] text-white border-2 border-[#1E1E24] rounded-full hover:bg-[#FF5A5F] hover:border-[#FF5A5F] transition-all font-bold tracking-wide px-8 py-3 disabled:opacity-60 whitespace-nowrap";

  return (
    <>
      <form
        onSubmit={onSubmit}
        data-testid={`${testIdPrefix}-form`}
        className="flex flex-col sm:flex-row gap-2"
      >
        <input
          data-testid={`${testIdPrefix}-input`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          className={inputCls}
        />
        <button
          data-testid={`${testIdPrefix}-submit`}
          type="submit"
          disabled={submitting}
          className={btnCls}
        >
          {submitting ? "..." : buttonLabel}
        </button>
      </form>
      {error && (
        <p className="text-[#FF5A5F] text-sm font-semibold mt-2 text-center">{error}</p>
      )}
      {showTrustLine && (
        <div
          className={`flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-sm mt-3 ${
            variant === "dark" ? "text-white/70" : "text-[#5E5E66]"
          }`}
        >
          <span>✓ No spam</span>
          <span>✓ Editor picks only</span>
          <span>✓ Unsubscribe anytime</span>
        </div>
      )}
    </>
  );
}
